import axios from "axios";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { API } from "../../context/AuthContext";
import { StatusBadge, PageCite, money } from "./StatusBadge";

const fmt = (v) => (v == null ? "" : Array.isArray(v) ? v.map((x) => (typeof x === "object" ? Object.values(x).join(" · ") : String(x))).join("\n") : typeof v === "object" ? Object.entries(v).map(([k, x]) => `${k}: ${x}`).join("\n") : String(v));
const th = "text-left text-[9px] font-mono uppercase tracking-wider text-gray-500 font-normal py-1.5 pr-3";
const iconBtn = "p-1 rounded border transition-colors duration-200";

export function FieldsTab({ fields, onSource }) {
  return (
    <div className="overflow-x-auto" data-testid="fields-tab">
      <table className="w-full text-xs">
        <thead><tr><th className={th}>Field</th><th className={th}>Extracted value</th><th className={th}>Conf.</th><th className={th}>Source</th></tr></thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.key} className="border-t border-white/5 align-top" data-testid={`field-row-${f.key}`}>
              <td className="py-2 pr-3 font-mono text-[11px] text-gray-400 whitespace-nowrap">{f.label}{f.custom && <span className="ml-1 text-sky-300">★</span>}</td>
              <td className="py-2 pr-3 text-gray-200 whitespace-pre-wrap max-w-[380px]">
                {f.status === "extracted" ? <span className="line-clamp-6">{fmt(f.value)}</span> : <span className="italic text-gray-500">{f.reason || "Not found"}</span>}
              </td>
              <td className="py-2 pr-3 font-mono text-[10px] text-gray-400">{f.confidence != null ? `${Math.round(f.confidence * 100)}%` : "—"}</td>
              <td className="py-2"><PageCite page={f.page} quote={f.quote} onOpen={onSource} testId={`field-cite-${f.key}`} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EventsTab({ events, canEdit, onSource, onChanged }) {
  const review = async (ev, action) => {
    try {
      await axios.post(`${API}/events/${ev.id}/review`, { action });
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update the event");
    }
  };
  if (!events.length) return <div className="text-xs italic text-gray-500 py-6" data-testid="events-tab-empty">No dated events from this document.</div>;
  return (
    <div className="space-y-2" data-testid="events-tab">
      {events.map((ev) => (
        <div key={ev.id} className="border border-white/5 rounded-lg p-3 bg-gray-950 flex gap-3" data-testid={`doc-event-${ev.id}`}>
          <div className="text-[10px] font-mono text-[#f59e0b] w-[84px] shrink-0">{ev.date}<div className="text-gray-500 mt-0.5">{(ev.date_role || "").replace(/_/g, " ")}</div></div>
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-white">{ev.title}</span>
              <StatusBadge status={ev.review_status} testId={`doc-event-status-${ev.id}`} />
              {ev.conflict && <span className="text-[9px] font-mono text-rose-300">conflict</span>}
            </div>
            {ev.original_date_text && <div className="text-[10px] font-mono text-gray-500">as written: “{ev.original_date_text}”</div>}
            <p className="text-[11px] text-gray-400 leading-relaxed">{ev.description}</p>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <PageCite page={ev.source_page} quote={ev.citation} onOpen={onSource} testId={`doc-event-cite-${ev.id}`} />
            {canEdit && ev.review_status !== "superseded" && (
              <div className="flex gap-1">
                <button onClick={() => review(ev, "approve")} title="Approve" data-testid={`doc-event-approve-${ev.id}`} className={`${iconBtn} text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/10`}><Check className="w-3 h-3" /></button>
                <button onClick={() => review(ev, "reject")} title="Reject" data-testid={`doc-event-reject-${ev.id}`} className={`${iconBtn} text-rose-300 border-rose-500/30 hover:bg-rose-500/10`}><X className="w-3 h-3" /></button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function LedgerTab({ ledger, reconciliation, onSource }) {
  return (
    <div className="space-y-3" data-testid="ledger-tab">
      {reconciliation && (
        <div className={`text-[11px] font-mono rounded-lg border px-3 py-2 ${reconciliation.balanced === false ? "border-rose-500/40 text-rose-300" : "border-emerald-500/30 text-emerald-300"}`} data-testid="reconciliation-box">
          {reconciliation.formula ? `${reconciliation.formula}: ${money(reconciliation.opening)} → expected ${money(reconciliation.expected_closing)} vs stated ${money(reconciliation.closing)} — ${reconciliation.balanced ? "RECONCILES" : `OFF BY ${money(reconciliation.difference)}`}` : "Totals check"}
          {reconciliation.ledger_credits != null && <div className="text-gray-400 mt-0.5">Extracted lines: credits {money(reconciliation.ledger_credits)} · debits {money(reconciliation.ledger_debits)}</div>}
        </div>
      )}
      {!ledger.length ? <div className="text-xs italic text-gray-500 py-4">No ledger entries from this document.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr><th className={th}>Date</th><th className={th}>Description</th><th className={th}>Category</th><th className={`${th} text-right`}>Amount</th><th className={th}>Src</th></tr></thead>
            <tbody>
              {ledger.map((l) => (
                <tr key={l.entry_id} className="border-t border-white/5" data-testid={`ledger-row-${l.entry_id}`}>
                  <td className="py-1.5 pr-3 font-mono text-[10px] text-gray-400 whitespace-nowrap">{l.date_iso || l.original_date_text || "undated"}</td>
                  <td className="py-1.5 pr-3 text-gray-200">{l.counterparty || l.description}{l.account_last4 && <span className="text-gray-500 font-mono"> ••{l.account_last4}</span>}</td>
                  <td className="py-1.5 pr-3"><span className={`text-[9px] font-mono uppercase ${l.category === "transfer" ? "text-sky-300" : l.category === "income" ? "text-emerald-300" : "text-gray-400"}`}>{l.category}{l.transfer_group_id ? " ⇄" : ""}</span></td>
                  <td className={`py-1.5 pr-3 text-right font-mono ${l.direction === "credit" ? "text-emerald-300" : "text-rose-300"}`}>{l.direction === "credit" ? "+" : "−"}{money(l.amount)}</td>
                  <td className="py-1.5"><PageCite page={l.source_ref?.page} quote={l.source_ref?.quote} onOpen={onSource} testId={`ledger-cite-${l.entry_id}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AuditTab({ audit, runs }) {
  return (
    <div className="space-y-4 text-xs" data-testid="audit-tab">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1">Extraction runs</div>
        {runs.map((r) => (
          <div key={r.run_id} className="font-mono text-[10px] text-gray-400 border-t border-white/5 py-1" data-testid={`run-row-${r.run_id}`}>
            {r.started_at?.slice(0, 19).replace("T", " ")} · {r.status} · {r.template_id || "—"} v{r.template_version || "—"} · {r.model_version || ""}{r.errors?.length ? ` · ${r.errors[0]}` : ""}
          </div>
        ))}
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1">Review & audit trail</div>
        {audit.map((a) => (
          <div key={a.action_id} className="border-t border-white/5 py-1.5 flex flex-wrap gap-x-3" data-testid={`audit-row-${a.action_id}`}>
            <span className="font-mono text-[10px] text-gray-500">{a.timestamp?.slice(0, 19).replace("T", " ")}</span>
            <span className="font-bold text-gray-200">{a.action.replace(/_/g, " ")}</span>
            <span className="text-gray-400">{a.actor?.email || a.actor?.user_id}</span>
            {a.reason && <span className="text-gray-400 italic">“{a.reason}”</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
